import React, { useEffect, useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { Calendar, ChevronLeft, ChevronRight, Clock3, Film, MapPin, RefreshCw } from "lucide-react";
import { cn } from "../utils";
import type { ScheduledScene, ShootDay, ShootingSchedule } from "../types";
import { SectionPillHeader } from "./SectionPillHeader";

type Props = {
  projectName: string;
  shootingSchedules: ShootingSchedule[];
};

type ViewMode = "month" | "week";

const summarizeLocations = (scenes: ScheduledScene[]) =>
  Array.from(
    new Set(
      scenes
        .map((scene) => scene.locationName || scene.setName || scene.locationRaw || "")
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  ).slice(0, 2);

export function ProjectShootCalendar({ projectName, shootingSchedules }: Props) {
  const activeSchedule = useMemo(() => {
    const sorted = [...shootingSchedules].sort((left, right) =>
      (right.importedAt || "").localeCompare(left.importedAt || ""),
    );
    return (
      sorted.find((schedule) => schedule.status === "confirmed") ||
      sorted.find((schedule) => schedule.status === "reviewed") ||
      sorted[0] ||
      null
    );
  }, [shootingSchedules]);

  const [viewMode, setViewMode] = useState<ViewMode>("month");
  const [anchorDate, setAnchorDate] = useState<Date>(() => {
    const seed = activeSchedule?.shootDays?.[0]?.shootDate;
    return seed ? parseISO(seed) : new Date();
  });
  const [selectedDayId, setSelectedDayId] = useState<string | null>(null);

  const shootDays = activeSchedule?.shootDays || [];
  const scenes = activeSchedule?.scheduledScenes || [];

  const selectedDay = useMemo(
    () => shootDays.find((day) => day.id === selectedDayId) || shootDays[0] || null,
    [selectedDayId, shootDays],
  );

  const scenesByDay = useMemo(() => {
    const map = new Map<string, ScheduledScene[]>();
    scenes.forEach((scene) => {
      if (!scene.shootDayId) return;
      const current = map.get(scene.shootDayId) || [];
      current.push(scene);
      map.set(scene.shootDayId, current);
    });
    return map;
  }, [scenes]);

  const daysToRender = useMemo(() => {
    if (viewMode === "week") {
      return eachDayOfInterval({
        start: startOfWeek(anchorDate, { weekStartsOn: 1 }),
        end: endOfWeek(anchorDate, { weekStartsOn: 1 }),
      });
    }
    return eachDayOfInterval({
      start: startOfWeek(startOfMonth(anchorDate), { weekStartsOn: 1 }),
      end: endOfWeek(endOfMonth(anchorDate), { weekStartsOn: 1 }),
    });
  }, [anchorDate, viewMode]);

  const shootDayByDate = useMemo(() => {
    const map = new Map<string, ShootDay>();
    shootDays.forEach((day) => {
      if (day.shootDate) map.set(day.shootDate, day);
    });
    return map;
  }, [shootDays]);

  useEffect(() => {
    const firstShootDate = activeSchedule?.shootDays?.[0]?.shootDate;
    if (firstShootDate) {
      setAnchorDate(parseISO(firstShootDate));
    }
    setSelectedDayId((current) =>
      current && shootDays.some((day) => day.id === current)
        ? current
        : shootDays[0]?.id || null,
    );
  }, [activeSchedule?.id, shootDays]);

  const summary = useMemo(() => {
    const scheduledScenes = scenes.filter((scene) => !scene.isOmitted && !scene.isUnscheduled);
    const locations = new Set(
      scheduledScenes
        .map((scene) => scene.locationName || scene.setName || scene.locationRaw || "")
        .map((value) => value.trim())
        .filter(Boolean),
    );
    return {
      shootDays: shootDays.length,
      scheduledScenes: scheduledScenes.length,
      omittedScenes: scenes.filter((scene) => scene.isOmitted).length,
      unscheduledScenes: scenes.filter((scene) => scene.isUnscheduled).length,
      locations: locations.size,
    };
  }, [scenes, shootDays]);

  if (!activeSchedule) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <SectionPillHeader
          icon={Calendar}
          title="Calendar"
          description={`No confirmed shooting schedule has been imported for ${projectName} yet. Import a one-line shooting schedule from Documents to build the shoot calendar.`}
        />
      </div>
    );
  }

  const selectedScenes = selectedDay ? scenesByDay.get(selectedDay.id) || [] : [];
  const locations = summarizeLocations(selectedScenes);

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <SectionPillHeader icon={Calendar} title="Calendar" />
            <h2 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{activeSchedule.title}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Imported from {activeSchedule.sourceFileName} and saved as {activeSchedule.status}.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-950">
              {(["month", "week"] as ViewMode[]).map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    viewMode === mode
                      ? "bg-[#ffcc01] text-slate-900"
                      : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200",
                  )}
                >
                  {mode === "month" ? "Monthly" : "Weekly"}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white p-1 dark:border-slate-700 dark:bg-slate-950">
              <button
                onClick={() => setAnchorDate((current) => (viewMode === "month" ? subMonths(current, 1) : addDays(current, -7)))}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="min-w-[140px] text-center text-sm font-semibold text-slate-800 dark:text-slate-100">
                {viewMode === "month" ? format(anchorDate, "MMMM yyyy") : `${format(startOfWeek(anchorDate, { weekStartsOn: 1 }), "dd MMM")} - ${format(endOfWeek(anchorDate, { weekStartsOn: 1 }), "dd MMM yyyy")}`}
              </div>
              <button
                onClick={() => setAnchorDate((current) => (viewMode === "month" ? addMonths(current, 1) : addDays(current, 7)))}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
          {[
            { label: "Shoot Days", value: summary.shootDays },
            { label: "Scheduled Scenes", value: summary.scheduledScenes },
            { label: "Locations", value: summary.locations },
            { label: "Omitted", value: summary.omittedScenes },
            { label: "Unscheduled", value: summary.unscheduledScenes },
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-950">
              <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">{item.label}</div>
              <div className="mt-2 text-2xl font-semibold text-slate-900 dark:text-slate-100">{item.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="grid grid-cols-7 border-b border-slate-200 dark:border-slate-800">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((label) => (
              <div key={label} className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                {label}
              </div>
            ))}
          </div>

          <div className={cn("grid", viewMode === "month" ? "grid-cols-7" : "grid-cols-7")}>
            {daysToRender.map((day) => {
              const iso = format(day, "yyyy-MM-dd");
              const shootDay = shootDayByDate.get(iso);
              const dayScenes = shootDay ? scenesByDay.get(shootDay.id) || [] : [];
              const dayLocations = summarizeLocations(dayScenes);
              return (
                <button
                  key={iso}
                  onClick={() => shootDay && setSelectedDayId(shootDay.id)}
                  className={cn(
                    "min-h-[140px] border-b border-r border-slate-200 p-3 text-left transition-colors dark:border-slate-800",
                    !isSameMonth(day, anchorDate) && viewMode === "month" ? "bg-slate-50/70 dark:bg-slate-950/40" : "bg-white dark:bg-slate-900",
                    isToday(day) && "ring-1 ring-inset ring-[#ffcc01]",
                    shootDay && "hover:bg-slate-50 dark:hover:bg-slate-950",
                  )}
                >
                  <div className="flex items-center justify-between">
                    <span className={cn("text-sm font-semibold", isToday(day) ? "text-[#ffcc01]" : "text-slate-700 dark:text-slate-200")}>
                      {format(day, "dd")}
                    </span>
                    {shootDay?.shootDayNumber ? (
                      <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">
                        Day {shootDay.shootDayNumber}
                      </span>
                    ) : null}
                  </div>

                  {shootDay ? (
                    <div className="mt-3 space-y-2">
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-2 text-slate-800">
                        <div className="text-xs font-semibold">{dayScenes.filter((scene) => !scene.isOmitted && !scene.isUnscheduled).length} scenes scheduled</div>
                        {shootDay.callTimeStart ? (
                          <div className="mt-1 flex items-center gap-1 text-[11px] text-slate-600">
                            <Clock3 className="h-3.5 w-3.5" />
                            {shootDay.callTimeStart}{shootDay.callTimeEnd ? ` - ${shootDay.callTimeEnd}` : ""}
                          </div>
                        ) : null}
                        {dayLocations.length > 0 ? (
                          <div className="mt-1 flex items-start gap-1 text-[11px] text-slate-600">
                            <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                            <span>{dayLocations.join(" • ")}</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-800">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Day Detail</div>
            {selectedDay ? (
              <>
                <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Shoot day {selectedDay.shootDayNumber || "?"} • {format(parseISO(selectedDay.shootDate), "EEEE dd MMM yyyy")}
                </h3>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {selectedDay.callTimeStart ? `${selectedDay.callTimeStart}${selectedDay.callTimeEnd ? ` - ${selectedDay.callTimeEnd}` : ""}` : "Call time not set"}
                  {selectedDay.dayType !== "unknown" ? ` • ${selectedDay.dayType}` : ""}
                </p>
              </>
            ) : (
              <h3 className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">Select a shoot day</h3>
            )}
          </div>

          <div className="space-y-4 p-5">
            {selectedDay ? (
              <>
                {selectedDay.dayNotes.length > 0 ? (
                  <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-300">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">Day Notes</div>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {selectedDay.dayNotes.map((note, index) => (
                        <li key={`${note}-${index}`}>{note}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="space-y-3">
                  {selectedScenes.length > 0 ? (
                    selectedScenes
                      .sort((left, right) => left.sortOrder - right.sortOrder)
                      .map((scene) => (
                        <div key={scene.id} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-950">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                                  {scene.originalSceneNumber || "Scene TBD"}
                                </span>
                                {scene.isOmitted ? (
                                  <span className="rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-rose-600">
                                    Omitted
                                  </span>
                                ) : null}
                                {scene.isUnscheduled ? (
                                  <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-600">
                                    Unscheduled
                                  </span>
                                ) : null}
                              </div>
                              <div className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                                {scene.sceneHeadingRaw || scene.description || scene.setName || "No scene heading"}
                              </div>
                              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                                {scene.locationName || scene.locationRaw ? (
                                  <span>{scene.locationName || scene.locationRaw}</span>
                                ) : null}
                                {scene.scriptPagesRaw ? <span>{scene.scriptPagesRaw} pgs</span> : null}
                                {scene.scriptDayRaw ? <span>{scene.scriptDayRaw}</span> : null}
                              </div>
                            </div>

                            {scene.linkedSceneId || scene.linkedSequenceTitle ? (
                              <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-right text-xs text-emerald-800">
                                <div className="font-semibold">Linked to bid</div>
                                <div className="mt-1">{scene.linkedSequenceTitle || scene.sceneIdNormalised}</div>
                              </div>
                            ) : (
                              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-right text-xs text-amber-800">
                                <div className="font-semibold">Needs review</div>
                                <div className="mt-1">No bid scene match yet</div>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                  ) : (
                    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                      No scheduled scenes recorded for this day yet.
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-400">
                Choose a shoot day from the calendar to inspect scenes, locations, and bid links.
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400">
              <div className="flex items-center gap-2 font-semibold uppercase tracking-[0.22em] text-slate-400">
                <RefreshCw className="h-3.5 w-3.5" />
                Source
              </div>
              <div className="mt-2">
                Imported from {activeSchedule.sourceFileName} on {format(parseISO(activeSchedule.importedAt), "dd MMM yyyy HH:mm")}.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
